import { faPen, faTrash, faPlus, faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Form, InputGroup, OverlayTrigger, Spinner, Tooltip } from "react-bootstrap";
import DataTable, { TableColumn } from "react-data-table-component";
import Loader from 'src/utils/Loader';
import RoleModal from "./RoleModal";
import DeleteRoleModal from "./DeleteRoleModal";
import EditRoleModal from "./EditRoleModal";
import { Role } from "src/interfaces/ManageUsersAndRole.interface";
import { useAppDispatch, useAppSelector } from "src/redux/storeHooks";
import { getRoleAsync, getRoleStaffList } from "src/redux/actions/manageUserActions";
import useAppToast from "src/hooks/useAppToast";
import { loggedInAdminAccessToken } from "src/utils/constants";
import { usePermission } from "src/hooks/usePermission";
import { useLocation, useNavigate } from "react-router-dom";

const formatDate = (dateString: string) => {
  const options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "short",
    day: "numeric",
  };
  return new Date(dateString).toLocaleDateString(undefined, options);
};

type ManageRolesFunctionalityProps = {
  setActiveView?: (view: "users" | "roles") => void;
};

const ManageRolesFunctionality = (props: ManageRolesFunctionalityProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const isAccountLocation = location.pathname === "/account/settings" || location.pathname === "/settings-old";
  const { isRead, isWrite, isDelete } = usePermission();
  const isLoginAdminAcess = !(localStorage.getItem(loggedInAdminAccessToken));
  const { fetchedUserAccountData } = useAppSelector((state) => state.userAccountData);
  const [loading, setLoading] = useState(false);
  const [openRoleModal, setOpenRoleModal] = useState(false);
  const [openRoleDeleteModal, setOpenRoleDeleteModal] = useState(false);
  const [openRoleEditModal, setOpenRoleEditModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [roleList, setRoleList] = useState<Role[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const { errorToast } = useAppToast();

  const isSuperAdmin = fetchedUserAccountData?.user_type === "SUPERADMIN" || fetchedUserAccountData?.account_type === 'ADMIN_STAFF';
  const userAccessLevel = fetchedUserAccountData?.role?.user_access?.toUpperCase();
  const canManageRoles = isSuperAdmin || userAccessLevel === "WRITE" || true;
  const canViewRoles = isSuperAdmin || userAccessLevel === "READ" || userAccessLevel === "WRITE" || true;
  const isSuperuser = fetchedUserAccountData?.is_superuser || fetchedUserAccountData?.account_type === 'ADMIN_STAFF';
  const isDirectUser = fetchedUserAccountData?.account_type === "DIRECT";

  const fetchRoleList = () => {
    if (isSuperuser) {
      setLoading(true);
      dispatch(getRoleAsync())
        .then((response) => {

          setRoleList(response.payload.data);
        })
        .catch((error) => {
          errorToast(error);
        })
        .finally(() => setLoading(false));
    } else {
      dispatch(getRoleStaffList({ paginationdata: { page_num: 1, page_limit: 10 } }))
        .then((response) => {
          setRoleList(response?.payload?.data);
        })
        .catch((error) => {
          errorToast(error);
        })
        .finally(() => setLoading(false));
    }
  };

  useEffect(() => {
    fetchRoleList();
  }, []);
  const [localRoles, setLocalRoles] = useState<Role[]>([]);
  const displayedRoles = (isSuperuser || !isLoginAdminAcess || isDirectUser) ? roleList : localRoles;

  const filteredRoles = useMemo(() => {
    if (!Array.isArray(displayedRoles)) return [];

    const term = searchTerm.trim().toLowerCase();
    if (!term) return displayedRoles;

    return displayedRoles.filter((role) => {
      // Always search role name
      if (role.name?.toLowerCase().includes(term)) return true;

      // SUPERUSER → static access search
      if (isSuperuser) {
        return [
          role.customer_access,
          role.manage_plan_access,
          role.user_access,
          role.report_access,
        ].filter(Boolean).some((value) => value!.toLowerCase().includes(term));
      }
      // NON-SUPERUSER → flow_and_permissions search
      if (!isSuperuser) {
        return [
          role.integration_access,
          role.other_access,
          role.product_access,
          role.sales_access,
          role.billing_access,
        ]
          .filter(Boolean)
          .some((value) => value!.toLowerCase().includes(term));
      }
      return false;
    });
  }, [displayedRoles, searchTerm, isSuperuser]);

  const clickManageUser = () => {
    // navigate("/manage-roles")
    props.setActiveView && props.setActiveView("users");
  }

  const handleEdit = useCallback(
    (role: Role) => {
      if (!canManageRoles) return;
      setSelectedRole(role);
      setOpenRoleEditModal(true);
    },
    [canManageRoles]
  );

  const handleDelete = useCallback(
    (role: Role) => {
      if (!canManageRoles) return;
      setSelectedRole(role);
      setOpenRoleDeleteModal(true);
    },
    [canManageRoles]
  );
  const columns: TableColumn<Role>[] = useMemo(() => {
    const baseColumns: TableColumn<Role>[] = [
      {
        name: "Role Name",
        selector: (row: Role) => row.name,
        sortable: true,
        width: "150px",
        minWidth: "180px",
        cell: (row: Role) => (
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip id={`role-name-${row.id}`}>{row.name}</Tooltip>}
          >
            <strong style={{ cursor: "pointer" }}>{row.name}</strong>
          </OverlayTrigger>
        ),
      },
    ];

    //  * SUPERUSER → STATIC COLUMNS
    if (isSuperuser) {
      baseColumns.push(
        {
          name: "Customer Access",
          selector: (row: Role) => row.customer_access ?? "-",
          width: "120px",
        },
        {
          name: "Plan Access",
          selector: (row: Role) => row.manage_plan_access ?? "-",
          width: "120px",
        },
        {
          name: "User Access",
          selector: (row: Role) => row.user_access ?? "-",
          width: "120px",
        },
        {
          name: "Report Access",
          selector: (row: Role) => row.report_access ?? "-",
          width: "120px",
        }
      );
    }

    //  * NON-SUPERUSER → DYNAMIC FLOW COLUMNS
    //  * NON-SUPERUSER → FIXED ACCESS COLUMNS (ORDERED)
    if (!isSuperuser) {
      const nonSuperuserColumns: TableColumn<Role>[] = [
        {
          name: "Integration Access",
          selector: (row) => row.integration_access ?? "-",
          width: "150px",
        },
        {
          name: "Other Access",
          selector: (row) => row.other_access ?? "-",
          width: "150px",
        },
        {
          name: "Product Access",
          selector: (row) => row.product_access ?? "-",
          width: "150px",
        },
        {
          name: "Sales Access",
          selector: (row) => row.sales_access ?? "-",
          width: "150px",
        },
        {
          name: "Billing Access",
          selector: (row) => row.billing_access ?? "-",
          width: "150px",
        },
        {
          name: "User Staff Management Access",
          selector: (row) => row.user_staff_management_access ?? "-",
          width: "150px",
        },
      ];

      baseColumns.push(...nonSuperuserColumns);
    }
    baseColumns.push(
      {
        name: "Created At",
        selector: (row: Role) => row.created_at,
        sortable: true,
        cell: (row: Role) => formatDate(row.created_at),
      },
      {
        name: "Updated At",
        selector: (row: Role) => row.updated_at,
        sortable: true,
        cell: (row: Role) => formatDate(row.updated_at),
      }
    );

    //  * ACTIONS COLUMN
    if (canManageRoles) {
      baseColumns.push({
        name: "Actions",
        cell: (row: Role) => (
          <div className="d-flex gap-3 align-items-center">
            {isRead && <FontAwesomeIcon
              icon={faPen}
              className="text-primary"
              title="Edit"
              style={{ cursor: "pointer" }}
              onClick={() => handleEdit(row)}
            />}
            {isDelete && <FontAwesomeIcon
              icon={faTrash}
              className="text-danger"
              title="Delete"
              style={{ cursor: "pointer" }}
              onClick={() => handleDelete(row)}
            />}
          </div>
        ),
        ignoreRowClick: true,
        allowOverflow: true,
        button: true,
      });
    }

    return baseColumns;
  }, [
    isSuperuser,
    canManageRoles,
    handleEdit,
    handleDelete,
    filteredRoles,
  ]);

  if (!canViewRoles) {
    return (
      <div className="container mt-4">
        <h4 className="fw-bold mb-4 text-dark" style={{ paddingLeft: isAccountLocation ? "4px" : "0px", paddingTop: isAccountLocation ? "3px" : "0px" }}
        >Manage Roles</h4>
        <div className="alert alert-warning">
          You do not have permission to view roles.
        </div>
      </div>
    );
  }

  return (
    <div className={isAccountLocation ? 'shadow rounded border-0 p-3' : ''}> 
    <div className="mb-2">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className={!isAccountLocation ? "fw-bold mb-0" : "mb-0"}
          style={isAccountLocation ? { fontSize: "15px", fontWeight: 650, paddingLeft: "12px" } : {}}
        >Manage Roles</h5>
        {!isSuperAdmin && !props.setActiveView && (<button className="btn btn-sm btn-primary" onClick={clickManageUser} >
          <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
          Back to Manage Users
        </button>)}
      </div>
      {/* {!isSuperAdmin && (<div className="text-muted" style={{ fontSize: "12px" }}>
          <span
            className="text-primary"
            style={{
              cursor: "pointer",
              transition: "color 0.15s ease, text-decoration 0.15s ease",
            }}
            onClick={() => navigate("/account/settings")}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = "#0d6efd";
              e.currentTarget.style.textDecoration = "underline";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = "inherit";
              e.currentTarget.style.textDecoration = "none";
            }}
          >
            <strong>User Management</strong>
          </span>
          <span className="mx-2">/</span>
          <span>Manage Roles</span>
        </div>)} */}
    </div>
      <div style={{ padding: isAccountLocation ? "7px" : "0px" }}>
        <div className={`card ${!isAccountLocation ? "shadow-sm  border-0" : ""}`} style={{
          boxShadow: isAccountLocation ? "0 -0.125rem 0.5rem rgba(0, 0, 0, 0.075)" : undefined,
          border: isAccountLocation ? "1px solid #ddd" : undefined, borderRadius: isAccountLocation ? "5px" : undefined,
        }}>
          <div className="card-header bg-white d-flex flex-wrap justify-content-between align-items-center gap-3">
            <InputGroup className={canManageRoles ? "w-25" : "flex-grow-1"}>
              <Form.Control
                placeholder="Search by role or access..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </InputGroup>
            <div className="d-flex flex-wrap gap-2">
              {/* {!isSuperAdmin && (
                <button
                  className="btn btn-sm btn-primary"
                  onClick={clickManageUser}
                >
                  <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                  Back to Users
                </button>
              )} */}
              {(isWrite && canManageRoles) && (
                <button
                  className="btn btn-sm btn-primary"
                  onClick={() => setOpenRoleModal(true)}
                >
                  <FontAwesomeIcon icon={faPlus} className="me-2" />
                  Add Role
                </button>
              )} </div>
          </div>

          <div className="card-body p-0">
            {loading ? (
              <div className="text-center py-5">
                <Loader variant="section" />
              </div>
            ) : (
              <DataTable
                columns={columns}
                data={filteredRoles || []}
                fixedHeader
                persistTableHead
                responsive
                pagination
                highlightOnHover
                striped
                noDataComponent={
                  <div style={{ textAlign: "center", width: "100%", padding: "20px" }}>
                    No roles found.
                  </div>
                }
                customStyles={{
                  table: { style: { minHeight: "55vh" } },
                  headRow: { style: { backgroundColor: "#fff", borderBottom: "1px solid #e5e7eb" } },
                  rows: { style: { minHeight: "48px", borderBottom: "1px solid #f3f4f6" } },
                  headCells: { style: { fontWeight: "bold", fontSize: "14px" } },
                }}
              />
            )}
          </div>
        </div></div>

      {/* Modals */}
      {canManageRoles && (
        <>
          <RoleModal
            show={openRoleModal}
            handleClose={() => setOpenRoleModal(false)}
            fetchData={fetchRoleList}
            isLoginAdminAcess={isLoginAdminAcess}
            addLocalRole={(newRole) => setLocalRoles((prev) => [...prev, newRole])}

          />
          <EditRoleModal
            show={openRoleEditModal}
            handleClose={() => setOpenRoleEditModal(false)}
            role={selectedRole}
            fetchData={fetchRoleList}
            isLoginAdminAcess={isLoginAdminAcess}

          />
          <DeleteRoleModal
            show={openRoleDeleteModal}
            handleClose={() => setOpenRoleDeleteModal(false)}
            role={selectedRole}
            fetchData={fetchRoleList}
          />
        </>
      )}
    </div>
  );
};

export default ManageRolesFunctionality;
